import React, { useEffect, useRef, useState } from 'react';
import { View, StyleSheet, TouchableOpacity } from 'react-native';
import TextM from '@common/components/text/TextM';
import { theme } from '@common/theme/theme';
import { FontAwesome5 } from '@expo/vector-icons';
import Animated, { 
  BounceInRight, 
  FadeOut,
  useSharedValue,
  withTiming,
  withRepeat,
  withSequence,
  withDelay,
  Easing,
  useAnimatedStyle
} from 'react-native-reanimated';
import { ProgressBarM } from '@common/components/progress/ProgressBarM';
import * as amplitude from '@amplitude/analytics-react-native';
import { hasFeature } from '@data/utility/Utils';
import {apiIdle, apiRequest} from "@data/redux/actions/api";
import {setIsLiveModeEnabled} from "@data/redux/actions/liveMode";
import {setMuteVideo} from "@data/redux/actions/videoCall";
import {connect} from "react-redux";

const TIMER_DURATION = 15000;
const TOTAL_STEPS = 50;

const ConversationSummaryPopup = ({ 
  visible, 
  onClose, 
  conversation,
  studentName,
  width = 380,
  maxHeight = 350,
  duration = 12000,
  showProgressBar = true,
  studentCardHeight = 180,
}) => {

  const [progress, setProgress] = useState(TOTAL_STEPS);
  const [isHovered, setIsHovered] = useState(false);
  const opacity = useSharedValue(visible ? 1 : 0);
  const scale = useSharedValue(1);
  const intervalRef = React.useRef(null);
  const lastProgressRef = React.useRef(TOTAL_STEPS);
  const warningIconOpacity = useSharedValue(1);
  const urgencyScale = useSharedValue(1);
  const borderPulse = useSharedValue(1);

  const isUrgent = conversation.status === "tutor_needed" ?? false

  const MAX_TEXT_LENGTH = 170;
  const summaryText = conversation?.summary || "";
  const truncatedSummary = summaryText.length > MAX_TEXT_LENGTH
      ? summaryText.substring(0, MAX_TEXT_LENGTH) + "..."
      : summaryText;
  const startTimer = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    const stepTime = TIMER_DURATION / TOTAL_STEPS;
    intervalRef.current = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev - 1;
        lastProgressRef.current = newProgress;
        if (newProgress <= 0) {
          clearInterval(intervalRef.current);
          onClose();
          return 0;
        }
        return newProgress;
      });
    }, stepTime);
  };

  const stopTimer = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  useEffect(() => {
    if (visible) {
      amplitude.track("checkpoint.summarypopup.display");

      opacity.value = withTiming(1, {
        duration: 150,
        easing: Easing.bezier(0.25, 0.1, 0.25, 1),
      });

      scale.value = withSequence(
          withTiming(1.05, { duration: 100 }),
          withTiming(1, { duration: 100 })
      );

      if (progress === TOTAL_STEPS) {
        setProgress(TOTAL_STEPS);
        lastProgressRef.current = TOTAL_STEPS;
      }

      if (!isHovered) {
        startTimer();
      }
    } else {
      opacity.value = withTiming(0, {
        duration: 200,
        easing: Easing.bezier(0.25, 0.1, 0.25, 1),
      });

      stopTimer();
      setProgress(TOTAL_STEPS);
      lastProgressRef.current = TOTAL_STEPS;
    }

    return () => {
      stopTimer();
    };
  }, [visible, opacity, scale, isHovered]);

  useEffect(() => {
    if (isHovered) {
      stopTimer();
    } else if (visible) {
      setProgress(lastProgressRef.current);
      startTimer();
    }
  }, [isHovered]);

  const animatedWarningIconStyle = useAnimatedStyle(() => {
    return {
      opacity: opacity.value,
      transform: [{ scale: scale.value }],
    };
  });
  
  const animatedBorderStyle = useAnimatedStyle(() => {
    return {
      borderColor: isUrgent 
        ? `rgba(239, 68, 68, ${0.6 / borderPulse.value})` 
        : 'rgba(203, 213, 225, 0.6)',
      shadowOpacity: isUrgent ? 0.15 * borderPulse.value : 0.08,
      shadowRadius: isUrgent ? 12 * borderPulse.value : 10,
    };
  });


  const getNonUrgentStyle = () => {
    return {
      borderColor: theme.colors.lighter,
      borderWidth: 1,
      backgroundColor: theme.colors.white,
      shadowOpacity: 0.08,
      shadowRadius: 10,
      shadowOffset: { width: 0, height: 4 },
    };
  };

  const getUrgentStyle = () => {
    return {
      borderColor: theme.colors.error,
      borderWidth: 2,
      backgroundColor: '#FFFBFA',
      shadowOpacity: 0.15,
      shadowRadius: 12,
      shadowOffset: { width: 0, height: 6 },
      shadowColor: theme.colors.error,
      borderRightWidth: 0,
    };
  };

  const popupContainerStyle = {
    ...styles.popupContainer,
    width: width,
    height: studentCardHeight,
    ...(isUrgent ? getUrgentStyle() : getNonUrgentStyle()),
  };

  const headerStyle = {
    ...styles.header,
    backgroundColor: isUrgent 
      ? theme.colors.errorLight
      : theme.colors.lighter,
    borderBottomWidth: 0,
    paddingVertical: 14,
  };

  const closeButtonStyle = {
    ...styles.closeButton,
    backgroundColor: isUrgent 
      ? theme.colors.errorLight
      : theme.colors.lighter,
    borderColor: isUrgent 
      ? theme.colors.errorLighter
      : 'rgba(148, 163, 184, 0.2)',
  };

  const progressContainerStyle = {
    ...styles.progressContainer,
    backgroundColor: theme.colors.white,
  };

  const progressBarStyle = {
    ...styles.progressBar,
    backgroundColor: theme.colors.white,
    height: 4,
  };

  const headerTextColor = isUrgent
    ? theme.colors.error
    : '#334155';
  
  const contentTextColor = isUrgent
    ? '#4B5563'
    : '#4B5563';

  const iconName = isUrgent ? "exclamation-circle" : "comment-alt";
  const iconColor = isUrgent ? theme.colors.error : theme.colors.grey;
  const iconContainerColor = isUrgent ? 'rgba(254, 202, 202, 0.8)' : 'rgba(241, 245, 249, 0.8)';


  const urgencyBadge = isUrgent && (
    <View style={styles.urgencyBadge}>
      <TextM 
        color={theme.colors.white}
        fontSize={10} 
        fontWeight="Bold"
      >
        URGENT
      </TextM>
    </View>
  );

  const enteringAnimation = isUrgent 
    ? BounceInRight.duration(600).springify().damping(14)
    : BounceInRight.duration(500).springify();
    
  const handleClose = () => {
    amplitude.track("checkpoint.summarypopup.close");
    onClose();
  };

  return (
    <View style={{...styles.container, height: studentCardHeight}}>
      <Animated.View
        entering={enteringAnimation}
        exiting={FadeOut.duration(100)}
        style={[popupContainerStyle, isUrgent && animatedBorderStyle]}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <View style={headerStyle}>
          <View style={styles.headerContent}>
            <View style={styles.titleContainer}>
              <View style={styles.titleRow}>
                <Animated.View 
                  style={[
                    styles.iconContainer, 
                    {backgroundColor: iconContainerColor},
                    isUrgent && animatedWarningIconStyle
                  ]}
                >
                  <FontAwesome5 
                    name={iconName} 
                    size={isUrgent ? 15 : 14} 
                    color={iconColor}
                  />
                </Animated.View>
                <View style={styles.headerTextContainer}>
                  <View style={{flexDirection: 'row', alignItems: 'center'}}>
                    <TextM 
                      color={headerTextColor} 
                      fontWeight="Bold" 
                      style={styles.headerLabel}
                    >
                      {conversation.summary_title || "Résumé de conversation"}
                    </TextM>
                    {urgencyBadge}
                  </View>
                  {studentName && (
                    <TextM 
                      color={theme.colors.grey}
                      fontSize={12} 
                      style={styles.studentName}
                      fontWeight="Medium"
                    >
                      {studentName}
                    </TextM>
                  )}
                </View>
              </View>
            </View>
          </View>
          <TouchableOpacity 
            onPress={handleClose} 
            style={closeButtonStyle}
          >
            <FontAwesome5 
              name="times" 
              size={11} 
              color={isUrgent ? theme.colors.error : '#94A3B8'}
            />
          </TouchableOpacity>
        </View>
        
        <View style={styles.contentWrapper}>
          <View style={[
            styles.contentContainer, 
            { 
              backgroundColor: isUrgent 
                ? 'rgba(255, 250, 250, 0.8)'
                : 'rgba(255, 255, 255, 0.8)'
            }
          ]}>
            <View style={styles.summaryContainer}>
              <TextM
                fontWeight= "Regular"
                fontSize={14}
                style={styles.summaryText}
                color={contentTextColor}
                wrap
                numberOfLines={8}
                ellipsizeMode="tail"
              >
                {truncatedSummary}
              </TextM>
            </View>
          </View>
        </View>
        
        {showProgressBar && (
          <View style={progressContainerStyle}>
            <ProgressBarM
              progressColor={isUrgent ? theme.colors.error : '#94A3B8'}
              count={progress}
              total={TOTAL_STEPS}
              barWidth={width}
              noText
              withAnimation={false}
              barStyle={progressBarStyle}
            />
          </View>
        )}
      </Animated.View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  iconContainer: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
  },
  headerTextContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  contentWrapper: {
    flex: 1,
    overflow: 'hidden',
  },
  urgencyBadge: {
    backgroundColor: '#EF4444',
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
    marginLeft: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  popupContainer: {
    backgroundColor: theme.colors.white,
    borderRadius: 16,
    shadowColor: theme.colors.black,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 8,
    borderWidth: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 14,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  titleContainer: {
    flex: 1,
  },
  headerLabel: {
    fontSize: 15,
    fontWeight: 'bold',
    lineHeight: 18,
    flex: 1,
  },
  studentName: {
    marginTop: 3,
    opacity: 0.9,
  },
  closeButton: {
    padding: 6,
    borderRadius: 12,
    marginLeft: 10,
    borderWidth: 1,
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  contentContainer: {
    padding: 16,
    height: '100%',
  },
  summaryContainer: {
    marginTop: 0,
  },
  summaryText: {
    lineHeight: 20,
  },
  progressContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    width: '100%',
    padding: 0,
    margin: 0,
    overflow: 'hidden',
    borderBottomLeftRadius: 16,
    borderBottomRightRadius: 16,
  },
  progressBar: {
    height: 4,
    borderRadius: 0,
    backgroundColor: theme.colors.white,
  },
});

export default ConversationSummaryPopup;